import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { FilterFormLoading, PanelLoading } from "~/components/loading";
import { ShareButton } from "~/components/share-button";

import type { EvidenceListFilters, SortDirection } from "~/features/evidences/types";
import { EvidencePeek } from "./EvidencePeek";
import { EvidencesSearchForm } from "./EvidencesSearchForm";
import { EvidencesTable } from "./EvidencesTable";

interface EvidencesPresenterProps {
  filters: EvidenceListFilters;
  onApplyFilters: (next: EvidenceListFilters) => void;
  direction: SortDirection;
  onSortingChange: (direction: SortDirection) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  isPending: boolean;
  onSelect: (id: string) => void;
  selectedEvidence: string | null;
  onCloseSelected: () => void;
}

export function EvidencesPresenter({
  filters,
  onApplyFilters,
  direction,
  onSortingChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  isPending,
  onSelect,
  selectedEvidence,
  onCloseSelected,
}: EvidencesPresenterProps) {
  const { t } = useTranslation();

  // Filter changes navigate (RR wraps it in a transition), so keying the Suspense boundary by the
  // filter signature remounts it and forces the shimmer; paging keeps the key stable and falls
  // through to the table's dimmed-rows pending state.
  const filterKey = JSON.stringify([filters.referenceIds, filters.userId]);

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <div className="flex items-center justify-between">
        <h1 className="text-dewey-title capitalize text-fg-1">{t("model.evidence.modelNames")}</h1>
        <ShareButton />
      </div>

      <div className="grid grid-cols-[240px_minmax(0,1fr)] gap-6">
        <aside className="min-w-0 overflow-hidden border-r border-border-default pr-4">
          <Suspense fallback={<FilterFormLoading />}>
            <EvidencesSearchForm filters={filters} onApply={onApplyFilters} />
          </Suspense>
        </aside>

        <main className="min-w-0">
          <Suspense key={filterKey} fallback={<PanelLoading />}>
            <EvidencesTable
              filters={filters}
              direction={direction}
              onSortingChange={onSortingChange}
              pageIndex={pageIndex}
              onPageIndexChange={onPageIndexChange}
              pageSize={pageSize}
              onPageSizeChange={onPageSizeChange}
              isPending={isPending}
              onSelect={onSelect}
            />
          </Suspense>
        </main>
      </div>

      <EvidencePeek id={selectedEvidence} onClose={onCloseSelected} />
    </div>
  );
}
